import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { SubstationService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { SubstationCrud, SubstationCrudCreate } from 'shared-lib';
import { Feeder33Service } from 'shared-lib';
import { F33Crud } from '../../../models/f33.model';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-ss',
  templateUrl: './crud-ss.component.html',
  styleUrl: './crud-ss.component.scss',
  standalone: false
})
export class CrudSsComponent implements OnInit, OnChanges {
  @Input() reloadTableInput: boolean = false;
  @Input() downloadClicked: boolean = false;

  currentPage: number = 1;
  pageSize: number = 10;
  count: number = 0;
  substations: SubstationCrud[] = [];
  utilityId: number = 1;
  searchText: string = "";

  updatedName: string = '';
  updatedNumber: string = '';
  feeders33: F33Crud[] = [];
  updatedF33Ids: number[] = [];

  ssToSave: SubstationCrudCreate = new SubstationCrudCreate();

  selectedSs: SubstationCrud = new SubstationCrud();
  isEditMode: boolean = false;

  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }
  
  @ViewChild('closeEditModal') closeEditModal: ElementRef;
  
  constructor(
    private substationService: SubstationService,
    private f33Service: Feeder33Service,
    private toastr: ToastrService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.reloadTable(1);
    this.getFeeders33();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadTableInput']) {
      setTimeout(() => {
        this.reloadTable(1);
      }, 500);
    } else if (changes['downloadClicked']) {
      const val = changes['downloadClicked'].currentValue;
      if (val) 
        this.download();    
    }
  }

  openAddModal() {
    this.isEditMode = false;
    this.selectedSs = new SubstationCrud();
    this.updatedName = '';
    this.updatedNumber = '';
    this.updatedF33Ids = [];
    this.feeders33 = this.feeders33.map(f => ({ ...f, checked: false }));
  }

  openEditModal() {
    this.isEditMode = true;
    this.fillInputFields();
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage; 
    this.reloadTable(newPage);
  }

  private getAllSubstations(dataIn: any) {
    this.substationService.getAll(dataIn).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.substations = responseData.data.data;
          this.substations.forEach((x) => {
            x.feedersNames = x.feeders?.map((y) => y.name).join(', ');
          });
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  getFeeders33() {
    this.f33Service.getAllForSelect(this.utilityId).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.feeders33 = responseData.data.map((f: F33Crud) => ({
            ...f,
            checked: false
          }));
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  deleteSs() {
    this.substationService.delete(this.selectedSs.id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.onPageChange(1);
          this.toastr.success(responseData?.message);
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  download() {
    const dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchText || '',
      },
      utilityId: this.utilityId,
    };

    this.substationService.download(dataIn).subscribe({
      next: (response: any) => {
        window.location.href = response.data;
      },
      error: (_) => {
        this.toastr.error('An error occurred.');
        this.reloadTable();
      }
    });
  }

  getFilterObject() {
    const obj = {
      searchValue: this.searchText
    };
    return obj;
  }

  mapSs() {
    this.ssToSave.id = this.isEditMode ? this.selectedSs.id : 0;
    this.ssToSave.name = this.updatedName;
    this.ssToSave.number = this.updatedNumber;
    this.ssToSave.feedersIds = this.updatedF33Ids;
    this.ssToSave.utilityId = this.utilityId;
  }

  save() {
    this.mapSs();
    if (this.ssToSave.name === '') {
      this.toastr.warning("Name can't be empty!");
    } else if (this.ssToSave.number === '') {
      this.toastr.warning("Number can't be empty!");
    } else if (this.ssToSave.feedersIds.length === 0) {
      this.toastr.warning("Please select at least one Main Feeder!");
    } else {
      this.substationService.save(this.ssToSave).subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.toastr.success(responseData?.message);
            setTimeout(() => {      
              this.reloadTable(1);
              this.closeEditModal?.nativeElement?.click();
            }, 500);
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occurred');
        },
      });
    }
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId
    };
    this.getAllSubstations(obj);
  }

  selectSs(item: SubstationCrud) {
    this.selectedSs = item;
    this.openEditModal();
  }

  onDeleteSs(item: SubstationCrud){
    this.selectedSs = item;
    this.isEditMode = false;
  }

  private fillInputFields() {
    this.updatedName = this.selectedSs.name;
    this.updatedNumber = this.selectedSs.number;
    this.updatedF33Ids = this.selectedSs.feeders.map(f => f.id);
    this.feeders33 = this.feeders33.map(f => ({
      ...f,
      checked: this.updatedF33Ids.includes(f.id)
    }));
  }

  onF33Select(itemList: any) {
    const selectedF33Names = itemList;
    this.updatedF33Ids = this.feeders33.filter(f => selectedF33Names.includes(f.name)).map(f => f.id);
  }

  onSearch() {
    this.reloadTable(1);
  }
}